import {isEqual, isUndefined} from 'lodash';
import React, {memo, useEffect, useState} from 'react';
import styled from 'styled-components';

import {EventHandler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {InteractiveList} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveList';
import {isWildcardToken, SearchToken} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {
  SearchSuggestionsBaseProps,
  SuggestionSelectHandler,
} from '../../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {getFlatOtherActiveTeammates, getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {getCellsSearchRecentQueries} from '../../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {trackSearchDropdownExpand} from '../../searchTrackingHelpers';
import {InboxesSearchSuggestionsDropdownExpandButton} from './inboxesSearchSuggestionsDropdownExpandButton';
import {InboxesSearchSuggestionsDropdownModifiers} from './inboxesSearchSuggestionsDropdownModifiers';
import {InboxesSearchSuggestionsDropdownTopResults} from './inboxesSearchSuggestionsDropdownTopResults';
import {SearchSuggestionsDropdownRecentQueries} from './searchSuggestionsDropdownRecentQueries';
import {SearchSuggestionsDropdownSuggestions} from './searchSuggestionsDropdownSuggestions';
import {useSearchSuggestions} from './useSearchSuggestions';

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownProps extends DropdownProps {
  queryHash?: string;
  query?: string;
  activeToken: SearchToken | undefined;
  namespace?: string;
  parameters: InboxesParameters;
  selectedSearchFilter: InboxesSearchLocationsEnum;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Style.
 */

const StyledSearchList = styled(InteractiveList)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdown = memo<InboxesSearchSuggestionsDropdownProps>((props) => {
  const {
    queryHash,
    query,
    activeToken,
    namespace,
    parameters,
    selectedSearchFilter,
    onSuggestionSelect,
    ...dropdownProps
  } = props;

  const user = useAppSelector(getUser);
  const teammates = useAppSelector(getFlatOtherActiveTeammates);
  const allRecentQueries = useAppSelector(getCellsSearchRecentQueries);

  const hasQuery = Boolean(query && query.trim().length > 0);
  const hasRecentQueries = Boolean(allRecentQueries?.length);
  const hasQueryOrRecentQueries = hasQuery || hasRecentQueries;

  const [isExpanded, setIsExpanded] = useState(false);
  const onToggleExpand: EventHandler = (event) => {
    // Keep the dropdown open.
    event.preventDefault();

    // Track.
    trackSearchDropdownExpand({
      queryHash,
      expand: !isExpanded,
    });

    // Toggle.
    setIsExpanded((wasExpanded) => !wasExpanded);
  };

  const {recentQueries, suggestions} = useSearchSuggestions(query, activeToken, namespace);

  const [stateQuery, setStateQuery] = useState(query);
  useEffect(() => {
    // If the new token is wilcard and is a "+", expand.
    if (activeToken && isWildcardToken(activeToken, query) && activeToken.value === '+') {
      setIsExpanded(true);
      setStateQuery(query);
    } else if (!isEqual(query, stateQuery)) {
      setIsExpanded(false);
      setStateQuery(query);
    }
  }, [activeToken, query, stateQuery]);

  if (!hasQueryOrRecentQueries) {
    // We need to set `isExclusive` to false here to be able to click on the filters dropdown button.
    return (
      <Dropdown isExclusive={false} {...dropdownProps}>
        <StyledSearchList filterValue={query} shouldSelectWithTab shouldSelectFirst>
          <InboxesSearchSuggestionsDropdownModifiers
            activeToken={{value: undefined}}
            onSuggestionSelect={onSuggestionSelect}
          />
        </StyledSearchList>
      </Dropdown>
    );
  }

  if (!activeToken) {
    // We need to set `isExclusive` to false here to be able to click on the filters dropdown button.
    return (
      <Dropdown isExclusive={false} {...dropdownProps}>
        <StyledSearchList filterValue={query} shouldSelectWithTab>
          <SearchSuggestionsDropdownRecentQueries
            recentQueries={recentQueries}
            onSuggestionSelect={onSuggestionSelect}
          />
          <InboxesSearchSuggestionsDropdownTopResults
            user={user}
            teammates={teammates}
            query={query}
            activeToken={{value: undefined}}
            parameters={parameters}
            selectedSearchFilter={selectedSearchFilter}
            onSuggestionSelect={onSuggestionSelect}
          />
        </StyledSearchList>
      </Dropdown>
    );
  }

  // Create new props to pass down as we're now sure that `activeToken` is defined.
  const suggestionProps: SearchSuggestionsBaseProps = {
    query,
    activeToken,
    onSuggestionSelect,
  };

  /* eslint-disable react/jsx-props-no-spreading */

  // We need to set `isExclusive` to false here to be able to click on the filters dropdown button.
  return (
    <Dropdown isExclusive={false} {...dropdownProps}>
      <StyledSearchList filterValue={query} shouldSelectWithTab shouldSelectFirst={isExpanded}>
        <SearchSuggestionsDropdownRecentQueries
          recentQueries={recentQueries}
          onSuggestionSelect={onSuggestionSelect}
        />
        {isExpanded && <InboxesSearchSuggestionsDropdownModifiers {...suggestionProps} />}
        {isWildcardToken(activeToken, query) && (
          <InboxesSearchSuggestionsDropdownExpandButton
            {...suggestionProps}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
        )}
        <SearchSuggestionsDropdownSuggestions
          activeToken={activeToken}
          suggestions={suggestions}
          onSuggestionSelect={onSuggestionSelect}
        />
        {!isExpanded && !isUndefined(activeToken.value) && (
          <InboxesSearchSuggestionsDropdownTopResults
            {...suggestionProps}
            user={user}
            teammates={teammates}
            parameters={parameters}
            selectedSearchFilter={selectedSearchFilter}
          />
        )}
      </StyledSearchList>
    </Dropdown>
  );
}, isEqual);
